import {
  Box,
  Button,
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { ActionIssueResolutionContent, IssueResolutionContent } from "features/Issues/Components/IssueCard";
import { IssueDetail } from "models/issue";
import { PropsWithChildren, useCallback, useRef } from "react";
import { ActionV2 } from "features/Resources/Shared/models";
import { sharedResourceKeys } from "features/Resources/Shared/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { ExpandedIssueTag } from "./IssueTag";
import { isTimingIssue } from "../helpers";
import { issueKeys } from "../hooks";
import { useIssueResolution } from "../Resolutions/useIssueResolution";

interface IssueModalProps extends PropsWithChildren {
  issue: IssueDetail;
  size?: string;
}

function IssueModalBase({ issue, size, children, ...rest }: IssueModalProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const queryClient = useQueryClient();
  const ref = useRef(null);

  const invalidateIssuesOnClose = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: issueKeys.byNewHireJourneyId(issue?.newHireJourneyId) });
    queryClient.invalidateQueries({ queryKey: issueKeys.detail(issue?.id) });
    queryClient.invalidateQueries({ queryKey: issueKeys.byActions() });
    queryClient.invalidateQueries({ queryKey: issueKeys.pendingIssuesByActions() });
    queryClient.invalidateQueries({ queryKey: sharedResourceKeys.byNewHireJourneys() });
    onClose();
  }, [issue?.id, issue?.newHireJourneyId, onClose, queryClient]);

  return (
    <>
      {issue && <ExpandedIssueTag issue={issue} onClick={() => onOpen()} {...rest} />}

      <Modal
        closeOnOverlayClick={false}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={false}
        onClose={onClose}
        size={size}
        isOpen={isOpen}
      >
        <ModalOverlay />
        <ModalContent ref={ref}>
          <ModalCloseButton
            fontSize="sm"
            // having issues with the modal close button size not working, this is a workaround
            sx={{ "--close-button-size": "var(--chakra-sizes-6)" }}
          />
          <ModalBody mt="6">
            <Center>
              <Box mt="4" w="100%">
                {children}
              </Box>
            </Center>
          </ModalBody>
          <ModalFooter>
            <Button onClick={invalidateIssuesOnClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default function IssueModalTag({ issue, ...rest }: { issue: IssueDetail }) {
  const resolution = useIssueResolution(issue);
  const size = resolution?.modalSize || (isTimingIssue(issue) ? "6xl" : "lg");

  return (
    <IssueModalBase issue={issue} size={size} {...rest}>
      <IssueResolutionContent issue={issue} includeNext={false} skeletonProps={{ width: "100%" }} />
    </IssueModalBase>
  );
}

export function ActionIssueModalTag({ issue, action, ...rest }: { issue: IssueDetail; action: ActionV2 }) {
  return (
    <IssueModalBase issue={issue} size="lg" {...rest}>
      <ActionIssueResolutionContent issueId={issue?.id} action={action} skeletonProps={{ width: "100%" }} />
    </IssueModalBase>
  );
}
